import {
  PIXEL_FONT_FAMILY,
  PIXEL_SCALE,
  getWorldPixelScale
} from './PixelGrid.js'

export class Renderer2D {
  constructor(container) {
    this.canvas = document.createElement('canvas')
    this.ctx = this.canvas.getContext('2d')
    this.canvas.className = 'game-canvas'
    this.container = container
    
    this.canvas.style.display = 'block'
    this.canvas.style.imageRendering = 'pixelated'
    container.appendChild(this.canvas)
    
    this.resize()
    window.addEventListener('resize', () => this.resize())
    
    // Camera offset (world position of top-left corner)
    this.cameraX = 0
    this.cameraY = 0
    
    // Tile size in pixels
    this.tileSize = 32
    
    // Zoom level
    this.zoom = 2
    this.defaultZoom = 2
    this.debugZoom = false
    this.pixelScale = PIXEL_SCALE

    // Global sprite palette remap for a cohesive retro pack look.
    this.spritePaletteRemap = new Map()
    this.spriteImageCache = new Map()
  }

  getSpriteImage(url) {
    if (this.spriteImageCache.has(url)) {
      return this.spriteImageCache.get(url)
    }

    const img = new Image()
    img.crossOrigin = 'anonymous'
    img.src = url
    this.spriteImageCache.set(url, img)
    return img
  }
  
  resize() {
    this.canvas.width = window.innerWidth
    this.canvas.height = window.innerHeight
    this.ctx.imageSmoothingEnabled = false
  }
  
  clear(color = '#1a1a2e') {
    this.ctx.fillStyle = color
    this.ctx.fillRect(0, 0, this.canvas.width, this.canvas.height)
  }
  
  // Convert world coordinates to screen coordinates
  worldToScreen(worldX, worldY) {
    const screenX = (worldX - this.cameraX) * this.tileSize * this.zoom + this.canvas.width / 2
    const screenY = (worldY - this.cameraY) * this.tileSize * this.zoom + this.canvas.height / 2
    return { x: screenX, y: screenY }
  }
  
  // Convert screen coordinates to world coordinates
  screenToWorld(screenX, screenY) {
    const worldX = (screenX - this.canvas.width / 2) / (this.tileSize * this.zoom) + this.cameraX
    const worldY = (screenY - this.canvas.height / 2) / (this.tileSize * this.zoom) + this.cameraY
    return { x: worldX, y: worldY }
  }
  
  // Center camera on a world position
  centerOn(worldX, worldY) {
    this.cameraX = worldX
    this.cameraY = worldY
  }

  withWorldTileBounds(bounds, draw) {
    if (!bounds || typeof draw !== 'function') return
    const topLeft = this.worldToScreen(bounds.minX, bounds.minY)
    const width = (bounds.maxX - bounds.minX + 1) * this.tileSize * this.zoom
    const height = (bounds.maxY - bounds.minY + 1) * this.tileSize * this.zoom

    this.ctx.save()
    this.ctx.beginPath()
    this.ctx.rect(topLeft.x, topLeft.y, width, height)
    this.ctx.clip()
    draw()
    this.ctx.restore()
  }
  
  // Draw a filled rectangle at world coordinates
  fillRect(worldX, worldY, width, height, color) {
    const screen = this.worldToScreen(worldX, worldY)
    const w = width * this.tileSize * this.zoom
    const h = height * this.tileSize * this.zoom
    
    this.ctx.fillStyle = color
    this.ctx.fillRect(screen.x, screen.y, w, h)
  }
  
  // Draw a tile at world coordinates with subtle sub-block texture
  drawTile(worldX, worldY, color) {
    const screen = this.worldToScreen(worldX, worldY)
    const size = this.tileSize * this.zoom
    
    // Base color
    this.ctx.fillStyle = color
    this.ctx.fillRect(screen.x, screen.y, size, size)
    
    // Subtle sub-block texturing - only a few cells get variation
    const subSize = size / 4
    for (let sy = 0; sy < 4; sy++) {
      for (let sx = 0; sx < 4; sx++) {
        const noise = this.getSubTileNoise(worldX, worldY, sx, sy)
        
        // Only vary ~20% of sub-cells
        if (noise > 0.9) {
          this.ctx.fillStyle = 'rgba(255,255,255,0.06)'
          this.ctx.fillRect(screen.x + sx * subSize, screen.y + sy * subSize, subSize, subSize)
        } else if (noise < 0.1) {
          this.ctx.fillStyle = 'rgba(0,0,0,0.06)'
          this.ctx.fillRect(screen.x + sx * subSize, screen.y + sy * subSize, subSize, subSize)
        }
      }
    }
    
    // Subtle edge darkening
    this.ctx.fillStyle = 'rgba(0,0,0,0.06)'
    this.ctx.fillRect(screen.x + size - 1, screen.y, 1, size)
    this.ctx.fillRect(screen.x, screen.y + size - 1, size, 1)
  }
  
  // Simple deterministic noise for sub-tile variation
  getSubTileNoise(x, y, sx, sy) {
    const n = Math.sin((x * 4 + sx) * 12.9898 + (y * 4 + sy) * 78.233) * 43758.5453
    return n - Math.floor(n)
  }
  
  // Simple deterministic noise based on position
  getTileNoise(x, y) {
    const n = Math.sin(x * 12.9898 + y * 78.233) * 43758.5453
    return n - Math.floor(n)
  }
  
  // Draw a sprite (pixel art data) at world coordinates
  drawSprite(sprite, worldX, worldY, direction = 0, scale = 1) {
    const screen = this.worldToScreen(worldX, worldY)

    if (sprite.imageUrl) {
      const img = this.getSpriteImage(sprite.imageUrl)
      if (!img.complete || img.naturalWidth === 0) {
        if (sprite.fallback) {
          this.drawPixelSprite(sprite.fallback, screen, direction, scale)
        }
        return
      }

      const frameWidth = sprite.frameWidth || sprite.width
      const frameHeight = sprite.frameHeight || sprite.height
      const pixelScale = this.getSpritePixelScale(scale)
      const drawWidth = frameWidth * pixelScale
      const drawHeight = frameHeight * pixelScale
      const { x: drawX, y: drawY } = this.getSpritePlacement(
        sprite,
        screen,
        drawWidth,
        drawHeight
      )
      this.ctx.save()
      this.ctx.translate(drawX + drawWidth / 2, drawY + drawHeight / 2)
      this.ctx.rotate(direction)
      this.ctx.translate(-drawWidth / 2, -drawHeight / 2)
      this.ctx.drawImage(
        img,
        sprite.frameX || 0,
        sprite.frameY || 0,
        frameWidth,
        frameHeight,
        0,
        0,
        drawWidth,
        drawHeight
      )
      this.ctx.restore()
      return
    }

    this.drawPixelSprite(sprite, screen, direction, scale)
  }

  drawPixelSprite(sprite, screen, direction = 0, scale = 1) {
    if (!sprite || !sprite.pixels) return

    const pixelSize = this.getSpritePixelScale(scale)
    const drawWidth = sprite.width * pixelSize
    const drawHeight = sprite.height * pixelSize
    const { x: drawX, y: drawY } = this.getSpritePlacement(
      sprite,
      screen,
      drawWidth,
      drawHeight
    )
    
    this.ctx.save()
    this.ctx.translate(drawX + drawWidth / 2, drawY + drawHeight / 2)
    this.ctx.rotate(direction)
    this.ctx.translate(-drawWidth / 2, -drawHeight / 2)
    
    for (let y = 0; y < sprite.height; y++) {
      for (let x = 0; x < sprite.width; x++) {
        const color = sprite.pixels[y * sprite.width + x]
        if (color && color !== 'transparent') {
          this.ctx.fillStyle = this.spritePaletteRemap.get(color) || color
          this.ctx.fillRect(x * pixelSize, y * pixelSize, pixelSize, pixelSize)
        }
      }
    }
    
    this.ctx.restore()
  }

  getSpritePixelScale(scale = 1) {
    return getWorldPixelScale(this.zoom, this.defaultZoom, scale)
  }

  getSpritePlacement(sprite, screen, drawWidth, drawHeight) {
    const boxSize = this.tileSize * this.zoom
    const anchorX = Math.round(screen.x + boxSize / 2)
    const anchorY = sprite.anchor === 'center'
      ? Math.round(screen.y + boxSize / 2)
      : Math.round(screen.y + boxSize)

    return {
      x: anchorX - Math.round(drawWidth / 2),
      y: sprite.anchor === 'center'
        ? anchorY - Math.round(drawHeight / 2)
        : anchorY - drawHeight
    }
  }
  
  // Draw a circle at world coordinates
  fillCircle(worldX, worldY, radius, color) {
    const screen = this.worldToScreen(worldX, worldY)
    const r = radius * this.tileSize * this.zoom
    
    this.ctx.fillStyle = color
    this.ctx.beginPath()
    this.ctx.arc(screen.x, screen.y, r, 0, Math.PI * 2)
    this.ctx.fill()
  }
  
  // Draw text at world coordinates
  drawText(text, worldX, worldY, color = 'white', fontSize = 12, align = 'center') {
    const screen = this.worldToScreen(worldX, worldY)
    const pixelScale = this.getSpritePixelScale()
    const displayFontSize = Math.max(
      pixelScale,
      Math.round((fontSize * this.zoom) / pixelScale) * pixelScale
    )

    this.ctx.fillStyle = color
    this.ctx.font = `${displayFontSize}px ${PIXEL_FONT_FAMILY}`
    this.ctx.textAlign = align
    this.ctx.fillText(text, Math.round(screen.x), Math.round(screen.y))
  }
  
  // Draw a health bar at world coordinates
  drawHealthBar(worldX, worldY, current, max, width = 1, height = 0.15) {
    const screen = this.worldToScreen(worldX, worldY)
    const w = width * this.tileSize * this.zoom
    const h = height * this.tileSize * this.zoom
    const percent = current / max
    
    // Background
    this.ctx.fillStyle = '#333'
    this.ctx.fillRect(screen.x - w/2, screen.y, w, h)
    
    // Fill
    this.ctx.fillStyle = percent > 0.5 ? '#4a4' : percent > 0.25 ? '#aa4' : '#a44'
    this.ctx.fillRect(screen.x - w/2, screen.y, w * percent, h)
    
    // Border
    this.ctx.strokeStyle = '#000'
    this.ctx.lineWidth = 1
    this.ctx.strokeRect(screen.x - w/2, screen.y, w, h)
  }
  
  // Apply a color overlay (for day/night)
  applyOverlay(color, alpha) {
    this.ctx.fillStyle = color
    this.ctx.globalAlpha = alpha
    this.ctx.fillRect(0, 0, this.canvas.width, this.canvas.height)
    this.ctx.globalAlpha = 1
  }
  
  // Apply dynamic lighting around a point (player light)
  applyPlayerLight(worldX, worldY, radius, intensity = 0.7) {
    const screen = this.worldToScreen(worldX, worldY)
    const r = radius * this.tileSize * this.zoom
    
    // Create radial gradient for light
    const gradient = this.ctx.createRadialGradient(
      screen.x, screen.y, 0,
      screen.x, screen.y, r
    )
    
    gradient.addColorStop(0, `rgba(0,0,0,0)`)
    gradient.addColorStop(0.5, `rgba(0,0,0,${intensity * 0.3})`)
    gradient.addColorStop(1, `rgba(0,0,0,${intensity})`)
    
    // Apply as darkness mask (everything outside light is darker)
    this.ctx.fillStyle = gradient
    this.ctx.fillRect(0, 0, this.canvas.width, this.canvas.height)
  }
  
  // Apply vignette effect (darker edges)
  applyVignette(intensity = 0.3) {
    const centerX = this.canvas.width / 2
    const centerY = this.canvas.height / 2
    const radius = Math.max(this.canvas.width, this.canvas.height) * 0.7
    
    const gradient = this.ctx.createRadialGradient(
      centerX, centerY, radius * 0.5,
      centerX, centerY, radius
    )
    
    gradient.addColorStop(0, 'rgba(0,0,0,0)')
    gradient.addColorStop(1, `rgba(0,0,0,${intensity})`)
    
    this.ctx.fillStyle = gradient
    this.ctx.fillRect(0, 0, this.canvas.width, this.canvas.height)
  }
  
  // Draw a raised block with shadow underneath for depth perception
  drawRaisedBlock(worldX, worldY, color, blockType) {
    const screen = this.worldToScreen(worldX, worldY)
    const size = this.tileSize * this.zoom
    const raiseOffset = 4 * this.zoom // How much the block appears raised
    
    // Draw shadow underneath (offset down and right)
    this.ctx.fillStyle = 'rgba(0,0,0,0.4)'
    this.ctx.fillRect(screen.x + raiseOffset, screen.y + raiseOffset, size, size)
    
    // Draw the raised block (offset up and left from shadow)
    this.ctx.fillStyle = color
    this.ctx.fillRect(screen.x, screen.y - raiseOffset, size, size)
    
    // Sub-block texture on the top face
    const subSize = size / 4
    for (let sy = 0; sy < 4; sy++) {
      for (let sx = 0; sx < 4; sx++) {
        const noise = this.getSubTileNoise(worldX, worldY, sx, sy)
        
        if (noise > 0.85) {
          this.ctx.fillStyle = 'rgba(255,255,255,0.08)'
          this.ctx.fillRect(screen.x + sx * subSize, screen.y - raiseOffset + sy * subSize, subSize, subSize)
        } else if (noise < 0.15) {
          this.ctx.fillStyle = 'rgba(0,0,0,0.08)'
          this.ctx.fillRect(screen.x + sx * subSize, screen.y - raiseOffset + sy * subSize, subSize, subSize)
        }
      }
    }
    
    // Draw front face (the "side" of the raised block)
    const frontColor = this.darkenColor(color, 0.3)
    this.ctx.fillStyle = frontColor
    this.ctx.fillRect(screen.x, screen.y - raiseOffset + size, size, raiseOffset * 2)
    
    // Top edge highlight
    this.ctx.fillStyle = 'rgba(255,255,255,0.25)'
    this.ctx.fillRect(screen.x, screen.y - raiseOffset, size, 2)
    this.ctx.fillRect(screen.x, screen.y - raiseOffset, 2, size)
    
    // Bottom/right edge of top face (inner shadow)
    this.ctx.fillStyle = 'rgba(0,0,0,0.15)'
    this.ctx.fillRect(screen.x + size - 2, screen.y - raiseOffset, 2, size)
    this.ctx.fillRect(screen.x, screen.y - raiseOffset + size - 2, size, 2)
  }
  
  // Helper to darken a hex color
  darkenColor(hex, amount) {
    // Parse hex color
    let r, g, b
    if (hex.length === 4) {
      r = parseInt(hex[1] + hex[1], 16)
      g = parseInt(hex[2] + hex[2], 16)
      b = parseInt(hex[3] + hex[3], 16)
    } else {
      r = parseInt(hex.slice(1, 3), 16)
      g = parseInt(hex.slice(3, 5), 16)
      b = parseInt(hex.slice(5, 7), 16)
    }
    
    // Darken
    r = Math.floor(r * (1 - amount))
    g = Math.floor(g * (1 - amount))
    b = Math.floor(b * (1 - amount))
    
    return `rgb(${r},${g},${b})`
  }
  
  // Get visible world bounds (for culling)
  getVisibleBounds() {
    const halfWidth = (this.canvas.width / 2) / (this.tileSize * this.zoom)
    const halfHeight = (this.canvas.height / 2) / (this.tileSize * this.zoom)
    
    return {
      minX: Math.floor(this.cameraX - halfWidth - 1),
      maxX: Math.ceil(this.cameraX + halfWidth + 1),
      minY: Math.floor(this.cameraY - halfHeight - 1),
      maxY: Math.ceil(this.cameraY + halfHeight + 1)
    }
  }
}
